import React, { memo } from 'react';
import { Platform, Pressable, View, useWindowDimensions } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SizableText, XStack, H6 } from 'tamagui';
import { useNavigation } from '@react-navigation/native';
import getDaysAgo from '@utils/getDaysAgo';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { googleAuths } from '@reduxLocal/selectors/userSelectors';
import * as Animatable from 'react-native-animatable';
import styles from '@stylesheet';


const unreadStr = 'UNREAD';
const inboxStr = 'INBOX';

interface MailItemProps {
  _handleMarking: (mail: any) => Promise<void>;
  itemSelected: any;
  setItemSelected: (item: any) => void;
  item: any;
  index: number;
  compactMode?: boolean;
  onDelete?: (mail: any) => void;
  onReply?: (mail: any) => void;
  onForward?: (mail: any) => void;
}

const RenderMailItems = ({ _handleMarking, itemSelected, setItemSelected, item, index, compactMode = false, onDelete, onReply, onForward }: MailItemProps) => {
  const theme = themeSelector();
  const googleObject = googleAuths();
  const accessToken = googleObject?.accessToken;
  const navigationRef = useNavigation();
  const { width } = useWindowDimensions();

  // Responsive variables
  const isSmallScreen = width < 768;
  const isMediumScreen = width >= 768 && width < 1024;
  const showCompactMode = compactMode || isSmallScreen;

  const unreadItem = item?.labelIds?.some(folder => {
    if (unreadStr?.includes(folder)) {
      return true;
    }
    return false;
  });

  const date = item.payload?.headers.find(date => date.name === 'Date')?.value;
  const subject = item.payload?.headers.find(subject => subject.name === 'Subject')?.value;
  const sender = item.payload?.headers.find(sender => sender.name === 'From')?.value?.split('<')[0]?.trim() || 'Unknown';
  const snippet = item?.snippet || '';

  // Mobile-only simplified view
  if (Platform.OS !== 'web') {
    return (
      <Animatable.View
        animation="fadeInUp"
        duration={600}
        delay={index * 50}
        style={[
          styles.modernCard,
          {
            margin: 4,
            minHeight: showCompactMode ? 80 : 100,
            backgroundColor: unreadItem ? (theme ? "#ffffff" : "#1a1a1a") : (theme ? "#f8f9fa" : "#2c2c2c"),
            borderLeftWidth: unreadItem ? 3 : 0,
            borderLeftColor: unreadItem ? "#3b82f6" : "transparent"
          }
        ]}
      >
        <Pressable
          onPress={() => {
            // @ts-ignore - Navigation typing issue
            navigationRef.navigate('IndividualMailScreen', { selectedMail: item, accessToken: accessToken });
          }}
          style={{
            flex: 1, 
            padding: showCompactMode ? 12 : 16,
          }}
        >
          <XStack justifyContent="space-between" alignItems="flex-start" marginBottom={showCompactMode ? 4 : 6}>
            <H6 color={theme ? "#1e293b" : "#f1f5f9"} fontSize={showCompactMode ? 14 : 16} fontWeight={unreadItem ? "600" : "400"}>
              {sender}
            </H6>
            <SizableText fontSize={showCompactMode ? 10 : 12} color={theme ? "#64748b" : "#94a3b8"}>
              {getDaysAgo(date)}
            </SizableText>
          </XStack>
          <SizableText color={theme ? "#1e293b" : "#f1f5f9"} fontSize={showCompactMode ? 13 : 14} fontWeight={unreadItem ? "600" : "400"} marginBottom={showCompactMode ? 4 : 6}>
            {subject || "No Subject"}
          </SizableText>
          <SizableText fontSize={showCompactMode ? 11 : 12} color={theme ? "#64748b" : "#94a3b8"} numberOfLines={showCompactMode ? 1 : 2}>
            {snippet}
          </SizableText>
        </Pressable>
      </Animatable.View>
    );
  }

  // Web view - modern card design
  if (!item) return null;

  const senderInitial = sender?.charAt(0)?.toUpperCase() || '?';

  // Action handlers
  const handleDelete = () => {
    if (onDelete) {
      onDelete(item);
    }
  };

  const handleReply = () => {
    if (onReply) {
      onReply(item);
    }
  };

  const handleForward = () => {
    if (onForward) {
      onForward(item);
    }
  };

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={600}
      delay={index * 50}
        style={[
          styles.modernCard,
          {
          margin: showCompactMode ? 4 : 8,
          minHeight: showCompactMode ? 70 : 90,
          backgroundColor: unreadItem && itemSelected?.id !== item.id
            ? (theme ? "#ffffff" : "#1a1a1a")
            : (theme ? "#f8f9fa" : "#2c2c2c"),
          borderLeftWidth: unreadItem ? 3 : 0,
          borderLeftColor: unreadItem ? "#3b82f6" : "transparent",
          transform: itemSelected?.id === item.id ? [{ scale: 0.98 }] : [{ scale: 1 }],
          opacity: itemSelected?.id === item.id ? 0.7 : 1,
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }
      ]}
    >
        <Pressable 
          onPress={() => {
            console.log('Mail item clicked:', item.id, subject);
            setItemSelected(item); 
            _handleMarking(item);
          }} 
        style={{
          flex: 1,
          padding: showCompactMode ? 10 : 14,
          flexDirection: 'row',
          alignItems: 'center',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0
        }}
      >
        {/* Avatar Section */}
        <View style={{
          width: showCompactMode ? 32 : 40,
          height: showCompactMode ? 32 : 40,
          borderRadius: showCompactMode ? 16 : 20,
          backgroundColor: theme ? '#e2e8f0' : '#334155',
          justifyContent: 'center',
          alignItems: 'center',
          marginRight: showCompactMode ? 10 : 12,
          borderWidth: 1,
          borderColor: theme ? '#e2e8f0' : '#334155',
          flexShrink: 0
        }}>
          <SizableText
            fontSize={showCompactMode ? 14 : 16}
            fontWeight="600"
            color={theme ? "#1e293b" : "#f1f5f9"}
          >
            {senderInitial}
          </SizableText>
        </View>

        {/* Content Section */}
        <View style={{
          flex: 1,
          marginRight: 8,
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          {/* Header Row */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: showCompactMode ? 2 : 3,
            width: '100%'
          }}>
            <View style={{ flex: 1, minWidth: 0, marginRight: 8 }}>
              <SizableText
                fontSize={showCompactMode ? 13 : 15}
                fontWeight={unreadItem ? "600" : "400"}
                color={theme ? "#1e293b" : "#f1f5f9"}
                numberOfLines={1}
              >
                {sender}
              </SizableText>
            </View>
            <SizableText fontSize={showCompactMode ? 10 : 11} color={theme ? "#64748b" : "#94a3b8"}>
              {getDaysAgo(date)}
            </SizableText>
          </View>

          {/* Subject */}
          <SizableText
            fontSize={showCompactMode ? 13 : 14}
            fontWeight={unreadItem ? "600" : "500"}
            color={theme ? "#1e293b" : "#f1f5f9"}
            marginBottom={showCompactMode ? 2 : 4}
            numberOfLines={1}
          >
            {subject || "No Subject"}
          </SizableText>

          {/* Snippet */}
          <SizableText
            fontSize={showCompactMode ? 11 : 12}
            color={theme ? "#64748b" : "#94a3b8"}
            numberOfLines={showCompactMode ? 1 : 2}
            lineHeight={showCompactMode ? 14 : 16}
          >
            {snippet}
          </SizableText>
        </View>

        {/* Status Indicator */}
        <View style={{
          alignItems: 'center',
          gap: showCompactMode ? 4 : 6,
          flexShrink: 0,
          width: showCompactMode ? 20 : 24
        }}>
          {unreadItem && (
            <View style={{
              width: showCompactMode ? 6 : 8,
              height: showCompactMode ? 6 : 8,
              borderRadius: showCompactMode ? 3 : 4,
              backgroundColor: "#3b82f6"
            }} />
          )}
          <MaterialIcons
            name="chevron-right"
            size={showCompactMode ? 16 : 18}
            color={theme ? "#64748b" : "#94a3b8"}
          />
          </View>
        </Pressable>

      {/* Action Buttons */}
      <View style={{
        flexDirection: 'row',
        padding: showCompactMode ? 8 : 12,
        gap: showCompactMode ? 6 : 8,
        borderTopWidth: 1,
        borderTopColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
      }}>
        <Pressable
          onPress={handleDelete}
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: showCompactMode ? 6 : 8,
            borderRadius: 6,
            backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
          }}
        >
          <MaterialIcons name="delete" size={showCompactMode ? 14 : 16} color="#ef4444" />
          <SizableText
            fontSize={showCompactMode ? 10 : 11}
            color="#ef4444"
            marginLeft={4}
          >
            Delete
          </SizableText>
        </Pressable>

        <Pressable
          onPress={handleReply}
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: showCompactMode ? 6 : 8,
            borderRadius: 6,
            backgroundColor: theme ? '#eff6ff' : '#1e3a8a',
          }}
        >
          <MaterialIcons name="reply" size={showCompactMode ? 14 : 16} color="#3b82f6" />
          <SizableText
            fontSize={showCompactMode ? 10 : 11}
            color="#3b82f6"
            marginLeft={4}
          >
            Reply
          </SizableText>
        </Pressable>

        <Pressable
          onPress={handleForward}
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            padding: showCompactMode ? 6 : 8,
            borderRadius: 6,
            backgroundColor: theme ? '#f0fdf4' : '#064e3b',
          }}
        >
          <MaterialIcons name="forward" size={showCompactMode ? 14 : 16} color="#10b981" />
          <SizableText
            fontSize={showCompactMode ? 10 : 11}
            color="#10b981"
            marginLeft={4}
          >
            Forward
          </SizableText>
        </Pressable>
      </View>
    </Animatable.View>
  )
}
export default memo(RenderMailItems) as React.FC<MailItemProps>;